import { useEffect, useRef } from 'react';

/** How much of an element must be showing before it animates. */
const REVEAL_THRESHOLD = 0.2;

/** Matches the `@for` loop in nb-base.scss — past 8 a grid reads as a wave. */
const MAX_STAGGER = 8;

/**
 * Gives every element that did not pick a `.nb-reveal--N` slot its own
 * `--nb-reveal-i`, counted among the reveals that share its parent. Counting
 * per parent rather than per root keeps two sibling grids from continuing each
 * other's stagger, which would leave the second one starting half a second in.
 */
const autoNumber = (targets: readonly HTMLElement[]): void => {
  const seen = new Map<Element, number>();

  targets.forEach((el) => {
    if (/nb-reveal--\d/.test(el.className)) return;

    const parent = el.parentElement;
    if (!parent) return;

    const index = seen.get(parent) ?? 0;
    seen.set(parent, index + 1);
    el.style.setProperty('--nb-reveal-i', String(Math.min(index, MAX_STAGGER - 1)));
  });
};

/**
 * Adds `is-in` to every `.nb-reveal` inside the returned ref once it scrolls
 * into view, which is what fires the fade-up animation.
 *
 * An element that does not name its own slot (`.nb-reveal--3`) is numbered
 * here, by its position among the `.nb-reveal` siblings sharing its parent, so
 * a plain list staggers without the markup counting anything out by hand.
 *
 * The observer unobserves each element after it lands: the reveal is a
 * one-shot, and leaving hundreds of entries registered on a long landing page
 * costs scroll performance for nothing.
 *
 * Elements are re-scanned whenever `deps` change, so sections that render
 * after an API call still animate.
 */
export const useScrollReveal = <T extends HTMLElement = HTMLDivElement>(
  deps: readonly unknown[] = [],
): React.RefObject<T> => {
  const rootRef = useRef<T>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const targets = Array.from(root.querySelectorAll<HTMLElement>('.nb-reveal:not(.is-in)'));
    if (targets.length === 0) return;

    autoNumber(targets);

    // No IntersectionObserver (very old browsers, jsdom): show everything
    // rather than leaving the page blank at opacity 0.
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: REVEAL_THRESHOLD, rootMargin: '0px 0px -40px 0px' },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // The dependency list is supplied by the caller, so the lint rule cannot
    // verify it statically. That is the point: a section decides for itself
    // when its content has changed enough to need a re-scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return rootRef;
};
